import React from 'react'
import { Button, Input, Label, Separator, Checkbox } from '@/shared/ui'
import type { Plan } from '../types'
import type { CryptoAsset } from '@/modules/crypto-assets/services/cryptoAssetsService'
import { listCryptoAssetsPaged } from '@/modules/crypto-assets/services/cryptoAssetsService'
import type { PlanAssetPolicyMode } from '../services/planAssetPolicyService'
import { getPlanAssetPolicy, upsertPlanAssetPolicy } from '../services/planAssetPolicyService'

type Props = {
  plan?: Plan | null
  onChanged?: () => void
}

type AssetPage = {
  items: CryptoAsset[]
  total: number
}

export default function PlanAssetAccessForm({ plan, onChanged }: Props) {
  const [mode, setMode] = React.useState<PlanAssetPolicyMode>('all')
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [loadingPolicy, setLoadingPolicy] = React.useState(false)
  const [loadingAssets, setLoadingAssets] = React.useState(false)
  const [assets, setAssets] = React.useState<AssetPage>({ items: [], total: 0 })
  const [page, setPage] = React.useState(0)
  const pageSize = 50
  const [query, setQuery] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [saved, setSaved] = React.useState(false)

  const hasPlan = !!plan?.id

  React.useEffect(() => {
    let mounted = true
    async function loadPolicy() {
      if (!plan?.id) {
        setMode('all')
        setSelected(new Set())
        return
      }
      setLoadingPolicy(true)
      setError(null)
      try {
        const policy = await getPlanAssetPolicy(plan.id)
        if (!mounted) return
        if (!policy) {
          setMode('all')
          setSelected(new Set())
        } else {
          setMode(policy.mode)
          setSelected(new Set(policy.allowed_symbols || []))
        }
      } catch (e: any) {
        if (!mounted) return
        const msg = String(e?.message || '')
        if (msg.includes('plan_asset_policies')) {
          // Tabela ainda nao existe no banco (migrations nao aplicadas).
          // Tratar como recurso desabilitado, sem quebrar o modal.
          setMode('all')
          setSelected(new Set())
          setError(
            'Recurso de moedas por plano ainda nao foi habilitado no banco (tabela plan_asset_policies ausente). ' +
            'Aplique as migrations mais recentes no Supabase para ativar.'
          )
        } else {
          setError('Falha ao carregar regras de moedas.')
        }
      } finally {
        if (mounted) setLoadingPolicy(false)
      }
    }
    loadPolicy()
    return () => { mounted = false }
  }, [plan?.id])

  React.useEffect(() => {
    let mounted = true
    async function loadAssets() {
      if (!plan?.id) {
        setAssets({ items: [], total: 0 })
        return
      }
      setLoadingAssets(true)
      setError(null)
      try {
        const { items, total } = await listCryptoAssetsPaged({ page, pageSize, q: query })
        if (!mounted) return
        setAssets({ items, total })
      } catch (e: any) {
        if (!mounted) return
        setError('Falha ao carregar ativos.')
        setAssets({ items: [], total: 0 })
      } finally {
        if (mounted) setLoadingAssets(false)
      }
    }
    loadAssets()
    return () => { mounted = false }
  }, [plan?.id, page, pageSize, query])

  React.useEffect(() => {
    // Reset de pagina ao mudar a busca
    setPage(0)
  }, [query])

  function toggleSymbol(symbol: string) {
    const key = String(symbol || '').trim().toUpperCase()
    if (!key) return
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function handleSave() {
    if (!plan?.id) return
    setSaving(true)
    setError(null)
    setSaved(false)
    try {
      const symbols = mode === 'limited' ? Array.from(selected.values()) : []
      await upsertPlanAssetPolicy({ planId: plan.id, mode, symbols })
      try { onChanged && onChanged() } catch {}
      setSaved(true)
    } catch (e: any) {
      const msg = String(e?.message || '')
      if (msg.includes('plan_asset_policies')) {
        setError(
          'Nao foi possivel salvar porque a tabela plan_asset_policies ainda nao existe no banco. ' +
          'Aplique as migrations mais recentes no Supabase para habilitar este recurso.'
        )
      } else {
        setError('Falha ao salvar regras de moedas.')
      }
    } finally {
      setSaving(false)
    }
  }

  const totalSelected = selected.size
  const totalPages = React.useMemo(
    () => (assets.total > 0 ? Math.max(1, Math.ceil(assets.total / pageSize)) : 1),
    [assets.total, pageSize]
  )

  return (
    <div className="mt-4 rounded-lg border border-border p-4">
      <div className="mb-2 text-lg font-semibold">Moedas por plano (Oportunidades)</div>
      {!hasPlan && (
        <div className="mb-3 text-sm text-foreground/70">
          Salve o plano primeiro para habilitar esta configuracao de moedas.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Modo de acesso</Label>
          <select
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={mode}
            onChange={(e) => setMode(e.target.value as PlanAssetPolicyMode)}
            disabled={!hasPlan || loadingPolicy || saving}
          >
            <option value="all">Todas as moedas ativas</option>
            <option value="limited">Apenas moedas selecionadas</option>
          </select>
          <p className="text-xs text-foreground/70">
            Quando limitado, membros so visualizam oportunidades e notificacoes das moedas selecionadas neste plano.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Resumo</Label>
          <p className="text-sm text-foreground/80">
            {mode === 'all'
              ? 'Plano sem restricao de moedas (todas as moedas liberadas).'
              : `Plano limitado a ${totalSelected} moeda(s) selecionada(s).`}
          </p>
        </div>
      </div>

      {mode === 'limited' && hasPlan && (
        <>
          <Separator className="my-4" />

          <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div className="flex-1 space-y-1.5">
              <Label>Buscar ativo</Label>
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ex.: BTC, ETH ou BTCUSDT"
              />
            </div>
            <div className="text-xs text-foreground/70 sm:ml-4 sm:text-right">
              {loadingAssets
                ? 'Carregando ativos...'
                : assets.total
                ? `${assets.total} ativo(s) encontrado(s)`
                : 'Nenhum ativo encontrado'}
            </div>
          </div>

          <div className="max-h-[320px] overflow-auto rounded-md border border-border/60">
            {loadingAssets ? (
              <div className="p-3 text-xs text-foreground/70">Carregando lista de ativos...</div>
            ) : assets.items.length === 0 ? (
              <div className="p-3 text-xs text-foreground/70">Nenhum ativo encontrado para o filtro atual.</div>
            ) : (
              <div className="divide-y divide-border/60">
                {assets.items.map((asset) => {
                  const sym = String(asset.symbol || '').toUpperCase()
                  const checked = selected.has(sym)
                  return (
                    <button
                      key={asset.id}
                      type="button"
                      onClick={() => toggleSymbol(sym)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/40"
                    >
                      <div className="flex min-w-0 items-center gap-3">
                        <Checkbox
                          checked={checked}
                          onChange={() => toggleSymbol(sym)}
                          className="pointer-events-none"
                        />
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{asset.name || sym}</div>
                          <div className="truncate text-xs text-foreground/60">
                            {sym}
                            {asset.base_asset && asset.quote_asset
                              ? ` / ${asset.base_asset}/${asset.quote_asset}`
                              : null}
</div>
                        </div>
                      </div>
                      <div className="text-[11px] text-foreground/60">
                        {checked ? 'Incluido' : 'Clique para incluir'}
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {assets.total > pageSize && (
            <div className="mt-2 flex items-center justify-between text-xs text-foreground/70">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page <= 0 || loadingAssets}
                className="rounded border border-border/60 px-2 py-1 hover:bg-muted/40 disabled:opacity-50"
              >
                Anterior
              </button>
              <div>
                Pagina {page + 1} de {totalPages}
              </div>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page + 1 >= totalPages || loadingAssets}
                className="rounded border border-border/60 px-2 py-1 hover:bg-muted/40 disabled:opacity-50"
              >
                Proxima
              </button>
            </div>
          )}
        </>
      )}

      {error && (
        <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}
      {!error && saved && (
        <div className="mt-3 rounded-md border border-emerald-300/40 bg-emerald-300/10 p-2 text-sm text-emerald-400">
          Regras de moedas salvas com sucesso.
        </div>
      )}

      <div className="mt-4 flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={!hasPlan || loadingPolicy || saving}
          onClick={() => {
            // Limpa selecoes locais (mudancas sao aplicadas apenas apos salvar)
            if (!plan?.id) return
            setSelected(new Set())
            setMode('all')
          }}
        >
          Limpar selecoes (modo atual)
        </Button>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!hasPlan || saving || loadingPolicy}
        >
          {saving ? 'Salvando regras...' : 'Salvar regras de moedas'}
        </Button>
      </div>
    </div>
  )
}


